import React, { useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Send, FileText, CheckCircle2, Upload } from "lucide-react";
import { SiretInput } from "@/components/SiretInput";
import { validateSiret } from "@/lib/siret";

const EMPTY = { prenom: "", nom: "", email: "", telephone: "", linkedin_url: "", company_name: "", siret: "", message: "", consent: false, website: "" };
const L = "text-[10px] uppercase tracking-[0.2em] font-semibold";

export const JobApplicationForm = ({ offer, spontaneous = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [cv, setCv] = useState(null);
  const [kind, setKind] = useState("emploi");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const bp = (spontaneous ? kind : offer.kind) === "business_partner";

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) { toast.error("Le CV (PDF) est obligatoire"); return; }
    if (cv.type !== "application/pdf" && !cv.name.toLowerCase().endsWith(".pdf")) { toast.error("Le CV doit être un fichier PDF"); return; }
    if (cv.size > 8 * 1024 * 1024) { toast.error("CV trop volumineux (8 Mo maximum)"); return; }
    if (bp || form.siret.trim()) {
      const s = validateSiret(form.siret);
      if (s.state !== "valid") { toast.error(bp && s.state === "empty" ? "Le SIRET est obligatoire pour une candidature Business Partner" : s.message); return; }
    }
    if (!form.consent) { toast.error("Merci d'accepter le traitement de vos données"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
      if (spontaneous) fd.append("kind", kind);
      fd.append("cv", cv);
      const { data } = await api.post(`/public/jobs/${spontaneous ? "spontaneous" : offer.id}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="bg-white border-2 border-[#059669] p-6" data-testid="job-apply-success">
        <CheckCircle2 className="h-8 w-8 text-[#059669]" />
        <div className="font-display font-black text-2xl tracking-tight mt-3">Candidature envoyée.</div>
        <p className="text-sm text-[#495057] mt-2">Merci {form.prenom} ! Votre référence : <b className="font-mono" data-testid="job-apply-reference">{done.reference}</b>. Un accusé de réception vous a été envoyé par email ; l'équipe PAG revient vers vous sous 10 jours ouvrés.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#DEE2E6] p-6 space-y-4" data-testid="job-apply-form">
      <div>
        <div className="font-display font-black text-xl tracking-tight">{spontaneous ? "Candidature spontanée" : "Postuler"}</div>
        <div className="text-[11px] text-[#868E96]">Réponse sous 10 jours ouvrés · CV PDF obligatoire{bp ? " · SIRET obligatoire" : ""}</div>
      </div>
      {spontaneous && (
        <div className="grid grid-cols-2 gap-2" data-testid="apply-kind">
          {[["emploi", "Un emploi chez PAG"], ["business_partner", "Un mandat Business Partner"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`apply-kind-${k}`} aria-pressed={kind === k}
                    className={`text-[11px] font-bold px-3 py-2 border transition ${kind === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className={L}>Prénom *</Label><Input value={form.prenom} onChange={(e) => set("prenom")(e.target.value)} required className="h-10" data-testid="apply-prenom" /></div>
        <div className="space-y-1.5"><Label className={L}>Nom *</Label><Input value={form.nom} onChange={(e) => set("nom")(e.target.value)} required className="h-10" data-testid="apply-nom" /></div>
      </div>
      <div className="space-y-1.5"><Label className={L}>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} required className="h-10" data-testid="apply-email" /></div>
      <div className="space-y-1.5"><Label className={L}>Téléphone *</Label><Input type="tel" value={form.telephone} onChange={(e) => set("telephone")(e.target.value)} required placeholder="06 12 34 56 78" className="h-10" data-testid="apply-telephone" /></div>
      <div className="space-y-1.5"><Label className={L}>LinkedIn (facultatif)</Label><Input value={form.linkedin_url} onChange={(e) => set("linkedin_url")(e.target.value)} placeholder="https://linkedin.com/in/…" className="h-10" data-testid="apply-linkedin" /></div>
      {bp && (
        <>
          <div className="space-y-1.5"><Label className={L}>Structure (raison sociale)</Label><Input value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} placeholder="Ex : Dupont Conseil EI" className="h-10" data-testid="apply-company" /></div>
          <SiretInput label="SIRET de votre structure" value={form.siret} onChange={set("siret")} required testid="apply-siret" hint="Obligatoire pour un mandat Business Partner" />
        </>
      )}
      <div className="space-y-1.5">
        <Label className={L}>CV (PDF, 8 Mo max) *</Label>
        <label className={`flex items-center gap-3 border-2 border-dashed p-3 cursor-pointer hover:border-[#002FA7] transition-colors ${cv ? "border-[#059669] bg-[#ECFDF5]" : "border-[#DEE2E6]"}`} data-testid="apply-cv-drop">
          {cv ? <FileText className="h-5 w-5 text-[#059669]" /> : <Upload className="h-5 w-5 text-[#868E96]" />}
          <span className="text-xs flex-1 truncate" data-testid="apply-cv-name">{cv ? `${cv.name} · ${(cv.size / 1024).toFixed(0)} Ko` : "Choisir mon CV au format PDF"}</span>
          <input type="file" accept="application/pdf,.pdf" className="sr-only" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="apply-cv" />
        </label>
      </div>
      <div className="space-y-1.5"><Label className={L}>Message (facultatif)</Label><Textarea value={form.message} onChange={(e) => set("message")(e.target.value)} rows={4} placeholder="Quelques lignes sur votre motivation…" data-testid="apply-message" /></div>
      <input type="text" name="website" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <label className="flex items-start gap-2 text-[11px] text-[#495057] cursor-pointer">
        <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent")(!!v)} data-testid="apply-consent" className="mt-0.5" />
        <span>J'accepte que PAG traite mes données et mon CV pour l'étude de ma candidature (conservation 24 mois max, suppression sur demande à contact@parcavenue.eu). *</span>
      </label>
      <Button type="submit" disabled={sending} className="w-full h-11 bg-[#002FA7] text-white" data-testid="apply-submit">
        <Send className="h-4 w-4 mr-2" />{sending ? "Envoi en cours…" : "Envoyer ma candidature"}
      </Button>
    </form>
  );
};
